import React, { useEffect } from 'react'
import OpenInBrowserIcon from 'components/icons/OpenInBrowserIcon'
import { IconButton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import DataViewBox from 'components/dataViewBox/DataViewBox'
import { formatNumberToNumberWithSpecifiedDecimals } from 'utils/commonUtils'
import DataViewBoxWithRows from 'components/dataViewBox/DataViewBoxWithRows'
import { Evidence, Relation } from 'evidenceTypes'
import {
    getFormattedRelationItem,
    getFormattedTestType,
    getTestTypeColor
} from 'utils/relationUtils'
import {} from 'mtcTypes'
import { MappedDriverItem } from 'reportViewer'
import { useEvidenceItemStyles } from './EvidencePage.styles'
import { RelationTypeMap, ResearchTypeMap } from './evidence.enums'

type Props = {
    evidenceItem: Evidence | null
    idx: number
    filter?: string
    listOpen: boolean
}

const EvidenceItem: React.FC<Props> = ({ evidenceItem, idx, listOpen }) => {
    const [isOpen, setIsOpen] = React.useState<boolean>(false)
    const [isEvidenceLinkOpening, setIsEvidenceLinkOpening] =
        React.useState<boolean>(false)

    const { t } = useTranslation('evidence')
    const styles = useEvidenceItemStyles()

    useEffect(() => {
        setIsOpen(listOpen)
    }, [listOpen])

    const toggleItem = () => {
        evidenceItem?.relations && setIsOpen(!isOpen)
    }

    const overallGenomateScore = evidenceItem?.sumOfRelationScores ?? 0
    const isNegative = overallGenomateScore < 0

    // const show = evidenceItem.type === filter

    // if (!show) return null

    const renderRelationsDetails = (item: Relation, key: number) => {
        const isItemNegative = item.level < 0
        const isDark = key % 2 === 0

        return (
            <div
                className={`${styles.row} ${isDark ? 'dark' : ''}`}
                key={JSON.stringify(item)}
            >
                <div className={styles.boxContent}>
                            {item.relationType && (
                        <DataViewBoxWithRows
                            classes={`relationBox ${RelationTypeMap[item.relationType]}`}
                            label={t('labels.relationType')}
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[{ value: item.relationType }]}
                        />
                    )}

                    {item.researchType && (
                        <DataViewBoxWithRows
                            classes="relationBox"
                            label={t('labels.researchType')}
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[
                                { value: ResearchTypeMap[item.researchType] }
                            ]}
                        />
                    )}

                    {item.tumor && (
                        <DataViewBoxWithRows
                            classes="relationBox"
                            label={t('labels.tumor')}
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[{ value: item.tumor }]}
                        />
                    )}

                    {item.histology && (
                        <DataViewBoxWithRows
                            classes="relationBox"
                            label={t('labels.histology')}
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[{ value: item.histology }]}
                        />
                    )}

                    {item.biomarker && (
                        <DataViewBoxWithRows
                            classes="relationBox"
                            label={
                                item.biomarker.biomarkerIsDriver
                                    ? t('labels.driver')
                                    : t('labels.biomarker')
                            }
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[renderRelationItem(item.biomarker)]}
                        />
                    )}

                    {item.target && (
                        <DataViewBoxWithRows
                            classes="relationBox"
                            label={t('labels.target')}
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[renderRelationItem(item.target)]}
                        />
                    )}

                    {item.molecularBackground?.length > 0 &&
                        item.molecularBackground.map((mb, mbIdx) => {
                            return (
                                <DataViewBoxWithRows
                                    key={mbIdx}
                                    classes="relationBox"
                                    label={t('labels.molecularBackground')}
                                    rowClasses={isDark ? 'dark' : 'light'}
                                    items={[renderRelationItem(mb)]}
                                />
                            )
                        })}

                    {item.compound && (
                        <DataViewBoxWithRows
                            classes="compundRelationBox"
                            label={t('labels.compound')}
                            rowClasses={isDark ? 'dark' : 'light'}
                            items={[renderCompound(item.compound)]}
                        />
                    )}
                </div>
                <div className={styles.genomateScore}>
                    <DataViewBox
                        classes={
                            isItemNegative
                                ? 'negativeGenomateScoreBox'
                                : 'positiveGenomateScoreBox'
                        }
                        label={t('labels.score')}
                        value={formatNumberToNumberWithSpecifiedDecimals(
                            item.level,
                            2
                        ).toString()}
                    />
                </div>
            </div>
        )
    }

    const renderRelationItem = (item: MappedDriverItem) => {
        if (item && item.testType && item.testResult) {
            return {
                value: getFormattedRelationItem(item),
                secondValue: getFormattedTestType(item.testType),
                secondValueColor: getTestTypeColor(item.testResult)
            }
        }

        return {
            value: getFormattedRelationItem(item)
        }
    }

    const renderCompound = (compound: string) => ({
        value: compound,
        secondValue: '',
        secondValueColor: 'pink'
    })

    const renderRelations = () => {
        if (!evidenceItem?.relations || evidenceItem.relations.length === 0)
            return null
        return (
            <div
                className={`${styles.details} ${isNegative ? 'negative' : ''}`}
            >
                {evidenceItem.relations.map(renderRelationsDetails)}
            </div>
        )
    }

    if (!evidenceItem)
        return (
            <div className={`${styles.item} evidence-item empty`}>
                <div className={styles.title}>
                    {t('noEvidenceAvailable')}
                </div>
                <div className={styles.score}>
                    <DataViewBox
                        classes="neutralGenomateScoreBox"
                        label={t('labels.score')}
                        value="N/A"
                    />
                </div>
            </div>
        )

    return (
        <React.Fragment>
            <div
                key={`${evidenceItem.title}-${idx}`}
                className={`${styles.item} ${isOpen ? 'open' : ''}${
                    isNegative ? ' negative' : ''
                } evidence-item`}
                onClick={toggleItem}
            >
                <IconButton
                    className={styles.iconWrapper}
                    size="medium"
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        window.open(evidenceItem.link, '_blank', 'noopener')
                        setIsEvidenceLinkOpening(true)
                        setTimeout(() => setIsEvidenceLinkOpening(false), 500)
                    }}
                    disabled={isEvidenceLinkOpening}
                >
                    <OpenInBrowserIcon fill="primary.main" />
                </IconButton>
                <div className={styles.title}>{evidenceItem.title}</div>

                {!isOpen ? (
                    <div className={styles.score}>
                        <DataViewBox
                            classes={
                                isNegative
                                    ? 'negativeGenomateScoreBox'
                                    : 'positiveGenomateScoreBox'
                            }
                            label={t('labels.score')}
                            value={formatNumberToNumberWithSpecifiedDecimals(
                                overallGenomateScore,
                                2
                            ).toString()}
                        />
                    </div>
                ) : null}
            </div>
            {isOpen ? renderRelations() : null}
        </React.Fragment>
    )
}

export default EvidenceItem
